<script setup lang="ts">
import StepperItem from '@/components/onlineSurvey/StepperItem.vue'
import FinishForm from '@/components/onlineSurvey/FinishTypes/FinishForm.vue'
import FinishRedirect from '@/components/onlineSurvey/FinishTypes/FinishRedirect.vue'
import FinishMessage from '@/components/onlineSurvey/FinishTypes/FinishMessage.vue'
import FinishFormAndMessage from '@/components/onlineSurvey/FinishTypes/FinishFormAndMessage.vue'
import ProgressBar from '@/components/onlineSurvey/questionTypes/ProgressBar.vue'
import { useSurvey } from '@/composables/onlineSurvey/useSurvey'
import { ref, computed, onMounted, provide, watch } from 'vue'
import { I18N_KEY, createTranslator, type Locale } from '@/i18n'
import api from '@/composables/onlineSurvey/api'

const n8nResult = ref<any>(null)
const submittingData = ref(false)
const error = ref(false)
const loadingError = ref(false)
const isLoading = ref(true)
const baseUrl = ref('')
const defaultSurveyId = '1c7aa13e-0724-4baa-983d-895a97d577da' // Fallback Survey ID

interface Props {
  theme?: 'light' | 'dark'
  primaryColor?: string
  secondaryColor?: string
  surveyId?: string
  environment?: 'production' | 'development' | 'test'
  variant?: 'default' | 'compact'
  language?: Locale
}

// Props mit Standardwerten
const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
  primaryColor: '#108cc7',
  secondaryColor: '#cf4a38',
  surveyId: '',
  environment: 'production',
  variant: 'default', // 'default' oder 'compact' ergänzt eine CSS Klasse für unterschiedliche Layouts
  language: 'de',
})

const t = computed(() => createTranslator(props.language ?? 'de'))
provide(I18N_KEY, (key, params) => t.value(key, params))

const {
  loadSurvey,
  surveyConfig,
  currentStep,
  answers,
  visibleQuestionIds,
  cleanupInvisibleAnswers,
} = useSurvey()
const finished = ref(false)

onMounted(() => {
  initSurvey()
})

async function initSurvey() {
  try {
    isLoading.value = true

    // URL basierend auf Umgebung wählen
    const baseUrls = {
      production: import.meta.env.VITE_API_BASE_URL_PROD,
      development: import.meta.env.VITE_API_BASE_URL_DEV,
      test: import.meta.env.VITE_API_BASE_URL_TEST,
    }

    baseUrl.value = baseUrls[props.environment]
    let surveyUrl = `${baseUrl.value}surveys/${props.surveyId || defaultSurveyId}`

    console.log('Fetching survey from URL:', surveyUrl, 'Environment:', props.environment)

    let response

    response = await api.get(surveyUrl)

    console.log('Fetching survey from URL:', response)
    const surveyResponse = response
    console.log('Survey response received:', surveyResponse)

    // Prüfen ob die Daten im erwarteten Format vorliegen
    let surveyArray
    let surveyMeta
    if (surveyResponse.data) {
      // Neue Struktur: response.data direkt
      surveyArray = surveyResponse.data.surveyArray
    }
    // Sicherstellen, dass surveyData ein Objekt und kein String ist
    if (typeof surveyArray === 'string') {
      surveyArray = JSON.parse(surveyArray)
    }

    surveyMeta = {
      n8nEndpoint: surveyResponse.data.n8nEndpoint ?? null,
      finish: {
        type: surveyResponse.data.finishType ?? null,
      },
      finishConfig: surveyResponse.data.finishConfig ?? null,
      benefitText: surveyResponse.data.benefitText ?? null,
      aiPrompt: surveyResponse.data.aiPrompt ?? null,
      thankYouMessage: surveyResponse.data.thankYouMessage ?? null,
    }

    loadSurvey(surveyArray, surveyMeta)
    loadingError.value = false
  } catch (e) {
    console.error('Could not load survey config from remote URL:', e)
    loadingError.value = true
  } finally {
    isLoading.value = false
    cleanupInvisibleAnswers()
  }
}

async function handleSurveyCompletion() {
  console.log('Survey completed, preparing to post data...', surveyConfig.value.finish.type)


  if (surveyConfig.value && surveyConfig.value.finish.type !== 'formular') {
    await postData()
  }
  if (
    surveyConfig.value.finish.type !== 'formular' &&
    surveyConfig.value.finish.type !== 'formularMessage'
  ) {
    await addSurveyLead(null)
  }
  finished.value = true
}

async function postData(leadUuid: string | null ) {
  console.log('Posting survey data to endpoint...')

  // Endpoint basierend auf Umgebung wählen
  const defaultEndpoints = {
    production: 'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7',
    development: 'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7',
    test: 'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7',
  }

  const payload = {
    answers: answers.value,
    surveyId: props.surveyId,
    aiPrompt: surveyConfig.value?.aiPrompt || null,
    benefitText: surveyConfig.value?.benefitText || null,
  }

  try {
    submittingData.value = true
    const endPoint = surveyConfig.value?.n8nEndpoint ?? defaultEndpoints[props.environment]

    console.log('Posting to endpoint:', endPoint, 'Environment:', props.environment)
    const result = await fetch(endPoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!result.ok) throw new Error(`HTTP error! status: ${result.status}`)

    // Versuche JSON zu parsen, falls Response Content-Type JSON ist
    let responseData
    const contentType = result.headers.get('content-type')

    if (contentType && contentType.includes('application/json')) {
      responseData = await result.json()
      console.log('Server response (JSON):', responseData)
      n8nResult.value = responseData
    } else {
      // Fallback für Text/HTML Response
      responseData = await result.text()
      console.log('Server response (Text):', responseData)
      n8nResult.value = responseData
    }

    if (leadUuid) await addSurveyLead(leadUuid)
  } catch (e) {
    console.error('Error posting survey data:', e)
    // Fallback-Response für Fehlerbehandlung
    n8nResult.value = {
      message: t.value('generalError'),
    }
    error.value = true
  } finally {
    submittingData.value = false
  }
}

const answeredQuestions = ref(0)

function updateAnsweredCount() {
  answeredQuestions.value = answers.value.length
  //scroll back tothe top of the container
  const hostElement = document.querySelector(`online-survey[survey-id="${props.surveyId}"]`)
  console.log('container scrollTo top', hostElement)
  if (hostElement) {
    hostElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start', // Scrollt zum Anfang der Component
      inline: 'nearest',
    })
  }
}

async function addSurveyLead(leadUuid: string | null) {
  try {
    console.log('Adding survey lead with answers:', answers.value)
    console.log('N8N Result:', n8nResult.value)

    const leadPayload = {
      surveyUuid: props.surveyId || defaultSurveyId,
      answerJson: JSON.stringify(answers.value),
      leadUuid: leadUuid,
      generatedAnswer: n8nResult.value?.output || "Redirected without AI response or AI response is empty",
    }

    console.log('Adding survey lead with payload:', leadPayload)
    // ${baseUrl.value}
    const leadEndpoint = ` ${baseUrl.value}surveys/leads`

    const parsedPayload = JSON.stringify(leadPayload)

    console.log(leadEndpoint)

    const result = await fetch(leadEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: parsedPayload,
    })

    if (result) {
      console.log('Survey lead added successfully:', result)
    } else {
      console.error('Failed to add survey lead - no result returned')
    }
  } catch (e) {
    console.error('Error adding survey lead:', e)
  }
}

const activeStep = computed(() => {
  if (!surveyConfig.value || !surveyConfig.value.steps) return null
  return surveyConfig.value.steps[currentStep.value] || null
})
</script>
<template>
  <!-- Loading State -->
  <div v-if="isLoading" class="online-survey" :class="`theme-${theme}`">
    <div class="p-6 has-text-centered">
      <div class="button is-primary is-loading is-large">{{ t('loading') }}</div>
    </div>
  </div>

  <!-- Error State -->
  <div v-else-if="loadingError" class="online-survey" :class="`theme-${theme}`">
    <div class="p-4">
      <div class="notification is-danger">
        <strong>{{ t('loadError') }}</strong>
        <p>{{ t('loadErrorMessage') }}</p>
      </div>
    </div>
  </div>

  <!-- Survey Content - nur wenn erfolgreich geladen -->
  <div
    v-else-if="surveyConfig && !loadingError"
    class="online-survey"
    :id="`online-survey-${props.surveyId}`"
    :class="`theme-${theme} variant-${variant}`"
    :style="{ '--primary-color': primaryColor, '--secondary-color': secondaryColor }"
  >
    <header
      class="is-flex is-flex-direction-column is-justify-content-center is-align-items-center px-4 py-4 has-text-centered"
    >
      <div v-if="currentStep == 0">
        <p class="is-size-4-desktop is-size-5-tablet has-text-weight-semibold">
          {{ surveyConfig.title }}
        </p>
        <p class="py-4">{{ surveyConfig.description }}</p>
      </div>
      <ProgressBar
        v-if="!finished"
        :answeredQuestions="answeredQuestions"
        :totalQuestions="visibleQuestionIds.size"
      />
    </header>

    <Transition name="fade" mode="out-in">
      <div
        v-if="!submittingData && activeStep && !finished"
        :key="activeStep.id"
        class="survey-step"
      >
        <StepperItem
          :step="activeStep"
          @surveryCompleted="handleSurveyCompletion"
          @moveSteps="updateAnsweredCount"
          class="p-4"
        />
      </div>
    </Transition>

    <!-- Post-Submit Error -->
    <div v-if="error && finished" class="p-4">
      <div class="notification">
        <strong>{{ t('sendError') }}</strong>
        <p>{{ n8nResult?.message || t('generalError') }}</p>
      </div>
    </div>
    <!-- Submitting State -->
    <div v-if="submittingData" class="p-6 has-text-centered">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p class="mt-4 is-size-5 has-text-weight-medium">{{ t('analyzing') }}</p>
        <p class="is-size-7 has-text-grey">{{ t('pleaseWait') }}</p>
      </div>
    </div>
    <!-- Finish States -->
    <div v-else-if="!submittingData && finished && !error" class="p-4 finish-state">
      <FinishRedirect
        v-if="surveyConfig.finish.type === 'redirect'"
        :redirectUrl="surveyConfig.finishConfig ?? ''"
      />
      <FinishMessage
        v-if="surveyConfig.finish.type === 'message'"
        :message="n8nResult?.output ?? t('defaultThankYou')"
        :thankYouMessage="surveyConfig.thankYouMessage"
      />
      <FinishForm
        v-if="surveyConfig.finish.type === 'formular'"
        @formCompleted="postData"
        :uuid="surveyConfig.finishConfig ?? ''"
        :theme="theme"
        :primaryColor="primaryColor"
        :message="n8nResult?.output"
        :baseUrl="baseUrl"
        :thankYouMessage="surveyConfig.thankYouMessage"
      />
      <FinishFormAndMessage
        v-if="surveyConfig.finish.type === 'formularMessage'"
        :uuid="surveyConfig.finishConfig ?? ''"
        :theme="theme"
        @formCompleted="addSurveyLead"
        :primaryColor="primaryColor"
        :message="n8nResult?.output"
        :baseUrl="baseUrl"
        :thankYouMessage="surveyConfig.thankYouMessage"
      />
    </div>
  </div>
</template>

<style>
/* Defensive CSS Reset für vollständige Isolation */
.online-survey {
  /* Box Model Reset */
  box-sizing: border-box;

  /* Typography Reset (Basierend auf Bulma Defaults) */
  font-family:
    BlinkMacSystemFont,
    -apple-system,
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'Fira Sans',
    'Droid Sans',
    'Helvetica Neue',
    'Helvetica',
    'Arial',
    sans-serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  letter-spacing: normal;
  text-transform: none;

  /* Colors */
  color: var(--bulma-text, #4a4a4a);
  background-color: transparent;

  /* Rendering optimizations */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-size-adjust: 100%;
}

/* Verhindert Box-Model Probleme */
.online-survey *,
.online-survey *::before,
.online-survey *::after {
  box-sizing: inherit;
}

/* Primary Color Override mit CSS Custom Properties */
.button.is-primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: white !important;
}

.button.is-loading {
  color: transparent !important;
}

.button.is-primary:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
  border-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
}

.button.is-primary:focus {
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--primary-color) 25%, transparent) !important;
}

/* Weißer Spinner für Primary Button */
.button.is-primary.is-loading::after {
  border-color: white !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
}

/* Custom Loading Spinner */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--bulma-border, #e0e0e0);
  border-radius: 50%;
  border-top-color: var(--primary-color, #3273dc);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Loading Spinner */
@media (max-width: 768px) {
  .loading-spinner {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }

  .loading-container {
    min-height: 150px;
    border-top-color: transparent !important;
  }
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.online-survey .input,
.online-survey button {
  align-items: center;
  appearance: none;
  border-radius: 0.375rem;
  border-style: solid;
  border-width: 1px;
  box-shadow: none;
  display: inline-flex;
  font-size: 1rem;
  height: 2.25rem;
  justify-content: flex-start;
  line-height: 1.5;
  padding: calc(0.75em - 1px);
  position: relative;
  transition-duration: 86ms;
  transition-property: background-color, border-color, box-shadow, color;
  vertical-align: top;
  border: 2px solid var(--bulma-border, #dbdbdb);
}

/* Dark Theme Inputs */
.theme-dark .input,
.theme-dark .textarea,
.theme-dark .select select {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}

.theme-dark .input:focus,
.theme-dark .textarea:focus,
.theme-dark .select select:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--primary-color) 25%, transparent) !important;
}

.theme-dark .input::placeholder,
.theme-dark .textarea::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.theme-dark .label {
  color: white !important;
}

/* Benefit Box Style */
.benefit {
  background-color: var(--bulma-scheme-main-bis, #fafafa);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border: 1px solid var(--bulma-border-weak, #f5f5f5);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.theme-dark .benefit {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.1);
  color: white;
}
</style>
